import React, { Fragment } from 'react';

import { Alert, AlertDescription, Grid, Margin } from '@zapier/design-system';

import Link from 'app/common/components/Link';
import NonEditableUI from 'app/developer-v3/components/NonEditableUI';
import { getAppUrl } from 'app/developer-v3/utils';
import {
  useCurrentVersionReadOnlyReason,
  useDismissable,
  useQueryImplementationCheck,
} from 'app/developer-v3/hooks';

import CTAAuth from './CTAAuth';
import CTAActions from './CTAActions';
import CTAPublish from './CTAPublish';
import CTAShare from './CTAShare';
import CTATriggers from './CTATriggers';
import CTAVerification from './CTAVerification';
import GoPublicTrigger from '../GoPublicTrigger';
import PrivateIntegrationMsg from '../PrivateIntegrationMsg';
import { getReadOnlyReasons } from './utils';

import type { ZapTaskDetails } from '../types';

type Props = {
  hasActions: boolean;
  hasAuthentication: boolean;
  hasSearches: boolean;
  hasTriggers: boolean;
  integrationId: number;
  isPrivate: boolean;
  isPrivateIntentionMsgVisible: boolean;
  isPublicIntention: boolean;
  isPublicIntentionElementVisible: boolean;
  onConfirmPublicIntention: () => Promise<void>;
  onDismissPrivateIntentionMsg: () => void;
  userCount: number;
  version: string;
  zapTaskDetails: ZapTaskDetails;
};

function GUI({
  hasActions,
  hasTriggers,
  hasAuthentication,
  integrationId,
  isPrivate,
  isPrivateIntentionMsgVisible,
  isPublicIntention,
  isPublicIntentionElementVisible,
  onConfirmPublicIntention,
  onDismissPrivateIntentionMsg,
  userCount,
  version,
  zapTaskDetails,
}: Props) {
  const { data, isLoading, isFetched } = useQueryImplementationCheck({
    integrationId,
    version,
  });
  const hasErrors = data?.hasErrors ?? false;
  const hasValidated = !isLoading && isFetched;

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const { isReadOnly, isLifecyclePending } = getReadOnlyReasons(
    currentVersionReadOnlyReason,
    userCount
  );

  const {
    dismiss: dismissVersionManagementMsg,
    isVisible: isVersionManagementMsgVisible,
  } = useDismissable({
    persistId: 'developer-version-overview-manage-version',
  });

  let ordinal = 1;

  return (
    <Fragment>
      {!isPublicIntentionElementVisible && isPrivateIntentionMsgVisible && (
        <Margin margin="0 0 30px">
          <PrivateIntegrationMsg onDismiss={onDismissPrivateIntentionMsg} />
        </Margin>
      )}
      {isReadOnly && <NonEditableUI />}
      {!isReadOnly && isVersionManagementMsgVisible && (
        <Margin margin="0 0 30px">
          <Alert onDismiss={dismissVersionManagementMsg}>
            <AlertDescription>
              If you want to update your integration without interrupting
              existing users, you can create a{' '}
              <Link href={`${getAppUrl(integrationId)}/versions`}>
                new version
              </Link>
              .
            </AlertDescription>
          </Alert>
        </Margin>
      )}
      <Grid columns={1} gap="15px 0px">
        <CTAAuth
          appId={integrationId}
          hasAuthentication={hasAuthentication}
          ordinal={ordinal++}
          userCount={userCount}
          version={version}
        />
        <CTATriggers
          appId={integrationId}
          hasAuthentication={hasAuthentication}
          hasTriggers={hasTriggers}
          ordinal={ordinal++}
          userCount={userCount}
          version={version}
          zapTaskDetails={zapTaskDetails.triggers}
        />
        <CTAActions
          appId={integrationId}
          hasActions={hasActions}
          hasAuthentication={hasAuthentication}
          ordinal={ordinal++}
          userCount={userCount}
          version={version}
          zapTaskDetails={zapTaskDetails.actions}
        />
        {!isLifecyclePending && (
          <CTAVerification
            hasAuthentication={hasAuthentication}
            hasValidated={hasValidated}
            isPrivate={isPrivate}
            ordinal={ordinal++}
          />
        )}
        {isPrivate &&
          !isLifecyclePending &&
          isPublicIntentionElementVisible && (
            <CTAPublish
              appId={integrationId}
              hasAuthentication={hasAuthentication}
              hasTriggers={hasTriggers}
              hasValidated={hasValidated}
              hasValidationErrors={hasErrors}
              ordinal={ordinal++}
              userCount={userCount}
            />
          )}
        {isPrivate &&
          !isLifecyclePending &&
          !isPublicIntentionElementVisible && (
            <CTAShare
              appId={integrationId}
              hasAuthentication={hasAuthentication}
              hasTriggers={hasTriggers}
              hasValidated={hasValidated}
              ordinal={ordinal++}
              userCount={userCount}
            />
          )}
      </Grid>
      {!isReadOnly && !isPublicIntention && (
        <GoPublicTrigger onConfirmIntention={onConfirmPublicIntention} />
      )}
    </Fragment>
  );
}

export default GUI;
